import ListTable from '../ListTable/ListTable'
import { useMemo } from 'react'
import { minimumRoles } from '../../config/OnboardingConfig'

const TenantTable = ({ data, loading, onSelectTenant }) => {

  const renderStatusWithTooltip = (tenant, label, colorClasses) => {
    const unified = Array.isArray(tenant?.unifiedRoles) ? tenant.unifiedRoles : []
    const roleIds = new Set(unified.map(r => r?.roleDefinitionId).filter(Boolean))
    const items = minimumRoles.map(r => ({ name: r.displayName, has: roleIds.has(r.id) }))
    return (
      <span className='relative group inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-sm select-none'>
        <span className={colorClasses}>{label}</span>
        <div className='absolute z-50 pointer-events-none hidden group-hover:block top-full mt-2 right-0 w-64 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-xl'>
          <p className='text-xs font-semibold mb-2 dark:text-gray-200'>GDAP Role Coverage</p>
          <ul className='grid grid-cols-1 gap-1'>
            {items.map(item => (
              <li key={item.name} className={`text-xs px-2 py-1 rounded ${item.has ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-700/30 dark:text-emerald-100' : 'bg-red-100 text-red-800 dark:bg-red-700/30 dark:text-red-100'}`}>
                {item.name}
              </li>
            ))}
          </ul>
        </div>
      </span>
    )
  }
  const columns = useMemo(() => [
    {
      header: 'Tenant Name',
      accessor: 'companyProfile.companyName',
      Cell: ({ row }) => (
        <div className='flex flex-col'>
          <span className='font-semibold text-inforcer-navy dark:text-gray-200 text-sm sm:text-base'>
            {row.original.companyProfile?.companyName || 'Unknown'}
          </span>
          <span className='text-xs italic text-gray-500 dark:text-gray-400'>
            {row.original.companyProfile?.domain || ''}
          </span>
          <span className='text-[11px] italic text-gray-400 dark:text-gray-500'>
            {row.original.companyProfile?.tenantId || row.original.id || ''}
          </span>
        </div>
      ),
    },
    {
      header: 'Search',
      accessor: 'searchText',
      enableColumnFilter: false,
      enableGlobalFilter: true,
      Cell: () => null,
      size: 0,
    },
    {
      header: 'Tenant GDAP Status',
      accessor: 'gdapStatus',
      
      Cell: ({ row }) => {
        const { unifiedRoles, tenantMissingRoles } = row.original
        if (Array.isArray(unifiedRoles) && unifiedRoles.length > 0) {
          return (tenantMissingRoles?.length || 0) === 0
            ? renderStatusWithTooltip(
                row.original,
                'Ready',
                'inline-flex items-center justify-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-emerald-700 dark:bg-emerald-700/30 dark:text-emerald-100'
              )
            : renderStatusWithTooltip(
                row.original,
                `Missing ${tenantMissingRoles?.length}`,
                'inline-flex items-center justify-center rounded-full bg-amber-100 px-2.5 py-0.5 text-amber-700 dark:bg-amber-700/30 dark:text-amber-100'
              )
        }
        return renderStatusWithTooltip(
          row.original,
          'No Relationship',
          'inline-flex items-center justify-center rounded-full bg-red-200 px-2.5 py-0.5 text-red-700 dark:bg-red-700/30 dark:text-red-100'
        )
      },
    },
    {
      header: 'Security Group GDAP',
      accessor: 'groupStatus',
      Cell: ({ row }) => {
        const status = row.original.groupStatus
        if (!status || status === 'No Group Roles') {
          return <span className='inline-flex items-center justify-center rounded-full bg-red-200 px-2.5 py-0.5 text-red-700 dark:bg-red-700/30 dark:text-red-100'>No Group Roles</span>
        }
        if (status?.startsWith('Missing')) {
          return <span className='inline-flex items-center justify-center rounded-full bg-amber-100 px-2.5 py-0.5 text-amber-700 dark:bg-amber-700/30 dark:text-amber-100'>{status}</span>
        }
        return <span className='inline-flex items-center justify-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-emerald-700 dark:bg-emerald-700/30 dark:text-emerald-100'>{status}</span>
      },
    },
    {
      header: 'Eligible Onboarders',
      accessor: 'eligibleOnboarders',
      Cell: ({ row }) => {
        const list = Array.isArray(row.original.eligibleOnboarders) ? row.original.eligibleOnboarders : []
        if (list.length === 0) {
          return (
            <span className='inline-flex items-center justify-center rounded-full bg-gray-100 px-2.5 py-0.5 text-gray-700 dark:bg-slate-700/40 dark:text-gray-200'>None</span>
          )
        }
        const preview = list.slice(0, 3)
        const remaining = list.length - preview.length
        return (
          <div className='flex items-center gap-1.5 flex-wrap max-w-[28ch]'>
            {preview.map(u => {
              const email = (u.userPrincipalName || u.mail || '').toLowerCase()
              return (
                <span key={u.id} className='inline-flex items-center rounded-full bg-sky-100 text-sky-800 px-2 py-0.5 text-xs dark:bg-sky-700/30 dark:text-sky-100'>
                  {email || (u.displayName || 'User')}
                </span>
              )
            })}
            {remaining > 0 && (
              <span className='inline-flex items-center rounded-full bg-gray-100 text-gray-700 px-2 py-0.5 text-xs dark:bg-slate-700/40 dark:text-gray-200'>
                +{remaining} more
              </span>
            )}
          </div>
        )
      },
    },
    {
      header: 'Auto Renewal',
      accessor: 'autoExtendDuration',
      Cell: ({ row }) => {
        const status = row.original.autoExtendDuration
        return status === 'P180D' ? (
          <span className='inline-flex items-center justify-center rounded-full bg-green-200 px-2.5 py-0.5 text-green-700 dark:bg-emerald-700/30 dark:text-emerald-100'>On</span>
        ) : (
          <span className='inline-flex items-center justify-center rounded-full bg-red-200 px-2.5 py-0.5 text-red-700 dark:bg-red-700/40 dark:text-red-100'>Off</span>
        )
      },
    },
  ], [])

  return (
    <ListTable
      uniqueKey='id'
      columns={columns}
      data={data}
      enableGlobalFilter
      enableColumnFilters
      loading={loading}
      initialState={{ columnVisibility: { searchText: false } }}
      onRowClick={tenant => onSelectTenant?.(tenant)}
    />
  )
}

export default TenantTable



